import type { PlayAudio } from '../components/ListenButton.js';
import { readSpeechRate } from './speech-rate.js';
import { levelFromTimeDomain } from './level.js';
import { audioContextOrNull } from '../browser.js';
import { playPcmStream } from './pcm-stream-player.js';
import { isPcmStream, wavBlobFrom } from './spoken-audio.js';

/**
 * Plays a turn's audio while reporting the real loudness of what is being heard.
 *
 * The level drives the interaction mark: it comes from a Web Audio analyser reading the actual
 * playback signal, never from a decorative animation pretending to be one. When the browser has no
 * `AudioContext`, or the caller asked for no levels, playback still works and simply reports
 * nothing — the mark falls back to its resting motion rather than to an invented waveform.
 *
 * Retention rules are the same as the plain player: the object URL is revoked and the audio
 * context closed whether playback ends, fails or never starts. The promise resolves when playback
 * is over, so a caller showing a "speaking" presence knows exactly when to stop.
 */
/**
 * The browser pieces, injected for the same reason `play-audio.ts` injects its own: no test
 * environment implements a media element or a Web Audio graph faithfully, and a player that reaches
 * for globals directly cannot be observed at all. Measured 2026-08-30 at a mutation score of zero —
 * not one of its sixty-seven mutants was killed, because nothing could reach the code to kill them.
 *
 * The defaults are exactly the globals this file used before, so every existing caller is unchanged.
 */
export interface AnalysedPlayerSeams {
  readonly createUrl: (audio: Blob) => string;
  readonly revokeUrl: (url: string) => void;
  readonly createAudio: (url: string) => HTMLAudioElement;
  readonly audioContext: (new () => AudioContext) | null;
  readonly requestFrame: (callback: () => void) => number;
  readonly cancelFrame: (handle: number) => void;
}

export const browserSeams = (): AnalysedPlayerSeams => ({
  createUrl: (audio) => URL.createObjectURL(audio),
  revokeUrl: (url) => {
    URL.revokeObjectURL(url);
  },
  createAudio: (url) => new Audio(url),
  audioContext: audioContextOrNull,
  requestFrame: (callback) => requestAnimationFrame(callback),
  cancelFrame: (handle) => {
    cancelAnimationFrame(handle);
  },
});

/** Plays a same-page Blob (object URL) or a remote https source with the same retention rules. */
async function playSource(
  seams: AnalysedPlayerSeams,
  source:
    | { readonly kind: 'blob'; readonly audio: Blob }
    | { readonly kind: 'url'; readonly url: string },
  onLevel?: (level: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const url = source.kind === 'blob' ? seams.createUrl(source.audio) : source.url;
  const revoke = source.kind === 'blob';
  const element = seams.createAudio(url);
  element.crossOrigin = 'anonymous';
  // The provider's own speed is already at its ceiling (1.2 is accepted, 1.3 refused), so anything
  // faster comes from playback. `preservesPitch` is what keeps a faster reading from sounding like
  // a different person; where a browser does not support it the audio simply plays at this rate.
  element.playbackRate = readSpeechRate();
  element.preservesPitch = true;

  let stopAnalysis = (): void => undefined;
  if (onLevel !== undefined && seams.audioContext !== null) {
    const context = new seams.audioContext();
    const source = context.createMediaElementSource(element);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    // The analyser taps the signal; the destination keeps it audible.
    source.connect(analyser);
    analyser.connect(context.destination);
    const samples = new Uint8Array(analyser.frequencyBinCount);
    let frame = 0;
    const tick = (): void => {
      analyser.getByteTimeDomainData(samples);
      onLevel(levelFromTimeDomain(samples));
      frame = seams.requestFrame(tick);
    };
    frame = seams.requestFrame(tick);
    // A click started this, which is what autoplay policy wants; resume covers a suspended start.
    void context.resume().catch(() => undefined);
    stopAnalysis = (): void => {
      seams.cancelFrame(frame);
      void context.close().catch(() => undefined);
    };
  }

  let released = false;
  const release = (): void => {
    if (released) return;
    released = true;
    stopAnalysis();
    if (revoke) seams.revokeUrl(url);
  };

  await new Promise<void>((resolve, reject) => {
    // Abort is a stop, not an error: the caller chose to end playback.
    signal?.addEventListener('abort', () => {
      element.pause();
      release();
      resolve();
    });
    element.addEventListener('ended', () => {
      release();
      resolve();
    });
    element.addEventListener('error', () => {
      release();
      resolve();
    });
    element.play().catch((failure: unknown) => {
      release();
      reject(failure instanceof Error ? failure : new Error('playback_failed'));
    });
  });
}

export function analysedBrowserAudioPlayer(seams: AnalysedPlayerSeams = browserSeams()): PlayAudio {
  return async (audio, onLevel, signal) => {
    if (!isPcmStream(audio)) return playSource(seams, { kind: 'blob', audio }, onLevel, signal);
    // Speech arriving as it is made is scheduled on the audio clock, so it is heard from its first
    // piece. Without an audio clock it is gathered into one file and played as any other.
    if (seams.audioContext === null)
      return playSource(
        seams,
        { kind: 'blob', audio: await wavBlobFrom(audio, signal) },
        onLevel,
        signal,
      );
    return playPcmStream(
      {
        audioContext: seams.audioContext,
        requestFrame: seams.requestFrame,
        cancelFrame: seams.cancelFrame,
        rate: () => readSpeechRate(),
      },
      audio,
      onLevel,
      signal,
    );
  };
}

/**
 * Plays a provider-hosted https source (the human recitation) with the same analyser and abort
 * semantics. Cross-origin sources without CORS keep playing but report silent levels — the mark
 * simply breathes at rest rather than pretending.
 */
export function playRecitationUrl(
  url: string,
  onLevel?: (level: number) => void,
  signal?: AbortSignal,
  seams: AnalysedPlayerSeams = browserSeams(),
): Promise<void> {
  if (!url.startsWith('https://')) return Promise.reject(new Error('recitation_not_https'));
  return playSource(seams, { kind: 'url', url }, onLevel, signal);
}
